import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";

import { GetProductOptions } from "../utils/product";


const COLOR_MAP = {
  "שחור": "#000000",
  "לבן": "#ffffff",
  "אפור": "#9e9e9e",
  "כחול": "#07345c",
  "אדום": "#e53935",
  "ורוד": "#ff69b4",
  "ירוק": "#43a047",
  "חום": "#6d4c41",
  "צהוב": "#fdd835",
  "כתום": "#fb8c00",
  "סגול": "#8e24aa",
  "תכלת": "#4fc3f7",

  "ורוד פוקסיה": "#ff1493",
  "טורקיז": "#246d66",
  "ירוק כהה": "#1b5e20",
  "סגול בהיר": "#ce93d8",
  "ורוד בהיר": "#f8bbd0",
  "כסף": "#c0c0c0",
  "זהב": "#e6c558",
  "נחושת": "#b87333",
  "ארד": "#cd7f32",
};


function getField(option, name) {
  return (
    option?.[name] ??
    option?.[
    name.charAt(0).toLowerCase() +
    name.slice(1)
    ]
  );
}


function normalizeOption(option) {
  return {
    id: getField(option, "Id"),

    optionType:
      getField(option, "OptionType"),

    value: String(
      getField(option, "Value") ?? ""
    ).trim(),

    extraPrice:
      getField(option, "ExtraPrice"),

    quantity:
      getField(option, "Quantity"),

    image:
      getField(option, "Image"),

    /*
     * חשוב:
     * אם השדה לא הגיע מהשרת,
     * ברירת המחדל תהיה שהווריאציה במלאי.
     */
    isInStock:
      getField(option, "IsInStock") ?? true,
  };
}


function Circle({
  children,
  selected,
  onClick,
  title,
  bg,
  disabled = false,
  textColor = "#111",
}) {
  const [tooltipOpen, setTooltipOpen] =
    useState(false);

  const accessibleLabel = disabled
    ? `${title} - לא במלאי`
    : `${title}${selected ? " - נבחר" : ""}`;

  const handleCircleClick = () => {
    if (disabled) {
      return;
    }

    onClick?.();

    // הצגת שם הצבע לאחר הלחיצה
    setTooltipOpen(true);

    window.setTimeout(() => {
      setTooltipOpen(false);
    }, 1500);
  };

  return (
    <Tooltip
      title={title || ""}
      placement="top"
      arrow
      open={tooltipOpen}
      onOpen={() => setTooltipOpen(true)}
      onClose={() => setTooltipOpen(false)}
      enterTouchDelay={0}
      leaveTouchDelay={1500}
    slotProps={{
  popper: {
    sx: {
      zIndex: 99999,
    },
  },

 tooltip: {
  sx: {
    fontSize: {
      xs: "0.75rem",
      sm: "0.8rem",
    },

    px: {
      xs: 1,
      sm: 1.25,
    },

    py: {
      xs: 0.5,
      sm: 0.6,
    },

    minHeight: "auto",
    lineHeight: 1.3,
    borderRadius: "5px",
    whiteSpace: "nowrap",
  },
},

  arrow: {
    sx: {
      fontSize: {
        xs: 8,
        sm: 10,
      },
    },
  },
}}
    >
      <Box
        component="button"
        type="button"
        disabled={disabled}
        onClick={handleCircleClick}
        onFocus={() => setTooltipOpen(true)}
        onBlur={() => setTooltipOpen(false)}
        aria-label={accessibleLabel}
        aria-pressed={selected}
        sx={{
          width: {
            xs: 24,
            sm: 26,
            md: 22,
          },
          height: {
            xs: 24,
            sm: 26,
            md: 22,
          },
          minWidth: {
            xs: 24,
            sm: 26,
            md: 22,
          },

          padding: 0,
          flexShrink: 0,
          borderRadius: "50%",
          cursor: disabled
            ? "not-allowed"
            : "pointer",

          userSelect: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          backgroundColor: bg,

         border: "1px solid #cfcfcf",

boxShadow: selected
  ? "0 0 0 2px #ffffff, 0 0 0 4px #000000"
  : "none",
          fontSize: 11,
          fontWeight: 700,
          color: textColor,
          opacity: disabled ? 0.35 : 1,
          position: "relative",

          "&:focus-visible": {
            outline: "3px solid #D81B60",
            outlineOffset: "2px",
          },

          "&:focus:not(:focus-visible)": {
            outline: "none",
          },

          "&:disabled": {
            cursor: "not-allowed",
          },

          "&::after": disabled
            ? {
                content: '""',
                position: "absolute",
                width: "130%",
                height: "2px",
                backgroundColor: "#d32f2f",
                transform: "rotate(-45deg)",
              }
            : {},
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}


/**
 * props:
 *
 * productId:
 * מזהה המוצר.
 *
 * onChange:
 * מחזירה את הווריאציות שנבחרו:
 * {
 *   Color?: option,
 *   Size?: option
 * }
 *
 * initialSelection:
 * מזהי הווריאציות שנבחרו מראש:
 * {
 *   Color?: optionId,
 *   Size?: optionId
 * }
 */
export default function ProductVariationPicker({
  productId,
  onChange,
  initialSelection,
}) {
  const [options, setOptions] =
    useState([]);

  const [selected, setSelected] =
    useState({});


  useEffect(() => {
    let cancelled = false;


    const load = async () => {
      try {
        const raw =
          await GetProductOptions(productId);


        const normalized =
          (raw || [])
            .map(normalizeOption)
            .filter(
              (option) =>
                option.optionType &&
                option.value
            );


        if (cancelled) {
          return;
        }


        setOptions(normalized);


        const nextSelected = {};


        const byType =
          normalized.reduce(
            (result, option) => {
              if (!result[option.optionType]) {
                result[option.optionType] = [];
              }

              result[option.optionType].push(
                option
              );

              return result;
            },
            {}
          );


        ["Color", "Size"].forEach(
          (type) => {
            const list =
              byType[type] || [];


            if (!list.length) {
              return;
            }


            const initialId =
              initialSelection?.[type];


            const found = initialId
              ? list.find(
                (option) =>
                  String(option.id) ===
                  String(initialId)
              )
              : null;


            /*
             * מחפשים את הווריאציה הראשונה
             * שמסומנת כזמינה במלאי.
             */
            const firstAvailable =
              list.find(
                (option) =>
                  option.isInStock !== false
              );


            /*
             * אם הבחירה הראשונית קיימת ובמלאי,
             * נבחר אותה.
             *
             * אחרת נבחר את האפשרות הראשונה
             * שבמלאי.
             *
             * אם כל האפשרויות לא במלאי,
             * נשאיר את הראשונה כדי שיוצג
             * ללקוח שהווריאציה אינה זמינה.
             */
            if (
              found &&
              found.isInStock !== false
            ) {
              nextSelected[type] = found;
            } else {
              nextSelected[type] =
                firstAvailable || list[0];
            }
          }
        );


        setSelected(nextSelected);

        onChange?.(nextSelected);
      } catch (error) {
        console.error(
          "Failed to load product options:",
          error
        );

        if (!cancelled) {
          setOptions([]);
          setSelected({});
          onChange?.({});
        }
      }
    };


    if (productId) {
      load();
    } else {
      setOptions([]);
      setSelected({});
      onChange?.({});
    }


    return () => {
      cancelled = true;
    };
  }, [
    productId,
    initialSelection,
    onChange,
  ]);


  const grouped = useMemo(() => {
    const byType = {
      Color: [],
      Size: [],
    };


    for (const option of options) {
      if (
        option.optionType === "Color"
      ) {
        byType.Color.push(option);
      } else if (
        option.optionType === "Size"
      ) {
        byType.Size.push(option);
      }
    }


    return byType;
  }, [options]);


  const handlePick = (
    type,
    option
  ) => {
    /*
     * לא ניתן לבחור וריאציה
     * שאינה במלאי.
     */
    if (
      option?.isInStock === false
    ) {
      return;
    }


    const next = {
      ...selected,
      [type]: option,
    };


    setSelected(next);

    onChange?.(next);
  };


  if (
    !grouped.Color.length &&
    !grouped.Size.length
  ) {
    return null;
  }


  return (
    <Box
      aria-label="אפשרויות המוצר"
      sx={{
        mt: 0,
        width: "100%",
        minWidth: 0,

        // תמיד נשמר אותו מקום לווריאציות
        height: {
          xs: 65,
          sm: 70,
          md: 70,
        },

        maxHeight: {
          xs: 65,
          sm: 70,
          md: 70,
        },

        overflowY: "visible",

        flexShrink: 0,
       
        overflowX: "hidden",
        boxSizing: "border-box",

        px: {
          xs: 0.25,
          sm: 0.5,
        },

        scrollbarWidth: "thin",
        scrollbarColor: "#D81B60 #f3f3f3",

        "&::-webkit-scrollbar": {
          width: 5,
        },

        "&::-webkit-scrollbar-track": {
          backgroundColor: "#f3f3f3",
          borderRadius: 4,
        },

        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#D81B60",
          borderRadius: 4,
        },
      }}
    >
      {grouped.Color.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            alignItems: "center",
            width: "100%",
            minWidth: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{ opacity: 0.8 }}
          >
            צבע
          </Typography>


          <Box
            sx={{
              display: "flex",
              flexDirection: "row",

              width: "100%",
              maxWidth: "100%",
              minWidth: 0,

              // הצבעים תמיד יישארו בשורה אחת
              flexWrap: "nowrap",

              // מתחילים מתחילת השורה
              justifyContent: "flex-start",
              alignItems: "center",

              gap: {
                xs: 0.5,
                sm: 0.75,
                md: 1,
              },

              px: 1,

              // משאיר מקום מלא למסגרת הפוקוס
              py: 0.75,

              overflowX: "auto",
              overflowY: "visible",

              direction: "rtl",
              boxSizing: "border-box",
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-x",

              scrollbarWidth: "thin",
              scrollbarColor: "#D81B60 #eeeeee",

              "&::-webkit-scrollbar": {
                height: 5,
              },

              "&::-webkit-scrollbar-track": {
                backgroundColor: "#eeeeee",
                borderRadius: 5,
              },

              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#D81B60",
                borderRadius: 5,
              },
            }}
          >
            {grouped.Color.map(
              (option) => {
                <Box
                  role="group"
                  aria-label="בחירת צבע"
                  sx={{
                    display: "flex",
                    flexDirection: "row",

                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,

                    flexWrap: "nowrap",
                    justifyContent: "flex-start",
                    alignItems: "center",

                    gap: {
                      xs: 0.5,
                      sm: 0.75,
                      md: 1,
                    },

                    px: 0.25,
                    pb: 0.5,

                    overflowX: "auto",
                    overflowY: "hidden",

                    direction: "rtl",
                    boxSizing: "border-box",
                    WebkitOverflowScrolling: "touch",
                    touchAction: "pan-x",

                    scrollbarWidth: "thin",
                    scrollbarColor: "#D81B60 #eeeeee",

                    "&::-webkit-scrollbar": {
                      height: 5,
                    },

                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#eeeeee",
                      borderRadius: 5,
                    },

                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#D81B60",
                      borderRadius: 5,
                    },
                  }}
                >
                  {grouped.Color.map((option) => {
                    const cssColor =
                      COLOR_MAP[option.value] ||
                      option.value;

                    const isSelected =
                      selected.Color?.id === option.id;

                    const isAvailable =
                      option.isInStock !== false;

                    return (
                      <Circle
                        key={
                          option.id ??
                          `color-${option.value}`
                        }
                        title={
                          isAvailable
                            ? option.value
                            : `${option.value} - לא במלאי`
                        }
                        selected={isSelected}
                        disabled={!isAvailable}
                        onClick={() =>
                          handlePick("Color", option)
                        }
                        bg={cssColor}
                        textColor="transparent"
                      >
                        .
                      </Circle>
                    );
                  })}
                </Box>
                const cssColor =
                  COLOR_MAP[option.value] ||
                  option.value;


                const isSelected =
                  selected.Color?.id ===
                  option.id;


                const isAvailable =
                  option.isInStock !== false;


                return (
                  <Circle
                    key={
                      option.id ??
                      `color-${option.value}`
                    }
                    title={
                      isAvailable
                        ? option.value
                        : `${option.value} - לא במלאי`
                    }
                    selected={isSelected}
                    disabled={!isAvailable}
                    onClick={() =>
                      handlePick(
                        "Color",
                        option
                      )
                    }
                    bg={cssColor}
                    textColor="transparent"
                  >
                    .
                  </Circle>
                );
              }
            )}
          </Box>
        </Box>
      )}


      {grouped.Size.length > 0 && (
        <Box
          sx={{
            mt: grouped.Color.length ? 1 : 0,

            display: "flex",
            flexDirection: "column",
            alignItems: "center",

            width: "100%",
            minWidth: 0,
            boxSizing: "border-box",

            // משאיר מקום לכיתוב לבחירה מעל המסגרת
            pt: {
              xs: 1,
              sm: 1,
            },

            px: 0.25,
            gap: 0,
          }}
        >
          <FormControl
            size="small"
            fullWidth
            sx={{
              width: "100%",
              minWidth: 0,
              maxWidth: {
                xs: "100%",
                sm: 180,
              },
            }}
          >
            <InputLabel
              id={`size-label-${productId}`}
            >
              לבחירה
            </InputLabel>


<Select
  labelId={`size-label-${productId}`}
  value={selected.Size?.id ?? ""}
  label="לבחירה"
  onChange={(event) => {
    const id = event.target.value;

    const option = grouped.Size.find(
      (item) => String(item.id) === String(id)
    );

    if (option) {
      handlePick("Size", option);
    }
  }}
  sx={{
    width: "100%",
    minWidth: 0,

    fontSize: {
      xs: "0.75rem",
      sm: "0.875rem",
    },

    // מרחיב בטלפון את הפתח במסגרת סביב המילה "לבחירה"
    "& .MuiOutlinedInput-notchedOutline legend > span": {
      paddingRight: {
        xs: "10px",
        sm: "5px",
      },
      paddingLeft: {
        xs: "10px",
        sm: "5px",
      },
    },

    "& .MuiSelect-select": {
      minWidth: "0 !important",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",

      py: {
        xs: 0.75,
        sm: 1,
      },

      px: {
        xs: 1,
        sm: 1.5,
      },
    },
  }}
>
              {grouped.Size.map(
                (option) => (
                  <MenuItem
                    key={
                      option.id ??
                      `size-${option.value}`
                    }
                    value={option.id}
                    disabled={
                      option.isInStock === false
                    }
                  >
                    {option.value}

                    {option.isInStock === false
                      ? " - לא במלאי"
                      : ""}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
        </Box>
      )}
    </Box>
  );
}